import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import BannerImage from '../../assets/nearby-merchants/banner-1.png';
import { ratingToPercent } from '../../utils/rating';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CartBar from '../../components/MerchantDetails/CartBar';
import MobileCartBar from '../../components/MerchantDetails/MobileCartBar';
import SingleProductDetails from '../../components/MerchantDetails/SingleProductDetails';
import Toast from '../../components/Toast';
import { useUser } from '../../contexts/user';
import { useCart } from '../../contexts/cart';
import useCartActions from '../../hooks/useCartActions';
import log from '../../utils/logger';
import DOMPurify from 'dompurify';
import api from '../../api/api';
import { useAppliedFilters } from '../../hooks/useAppliedFilters';
import { useFilterConfig } from '../../contexts/filters';
import { DRIVE_SPEED_KMH } from '../../config/appConfig';
import {
  partitionFilters,
  collectProductCategories,
  applyProductFilters,
} from '../../utils/filters';

const MerchantsDetails = () => {
  const { t, i18n } = useTranslation();
  const { merchantConfigId } = useParams();

  const [store, setStore] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [activeTab, setActiveTab] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [variantDecision, setVariantDecision] = useState(null);

  const categoryRefs = useRef({});

  const { user, idToken, adminImpersonate } = useUser();
  const { uniqueItems } = useCart();
  const { addToCart, getMaxVariantFromCart, getVariantQuantitiesFromCart, changeQuantity, toastMessage, toastType } = useCartActions();
  const appliedFilters = useAppliedFilters();
  const { setFilterConfig, resetFilterConfig } = useFilterConfig();

  const availableProductCategories = useMemo(
    () => collectProductCategories(allProducts, i18n.language),
    [allProducts, i18n.language],
  );

  useEffect(() => {
    setFilterConfig({ mode: 'products', categories: availableProductCategories });
    return () => resetFilterConfig();
  }, [availableProductCategories, setFilterConfig, resetFilterConfig]);

  const { sortBy, categorySlugs } = useMemo(
    () => partitionFilters(appliedFilters),
    [appliedFilters],
  );
  const hasActiveFilters = categorySlugs.size > 0 || Boolean(sortBy);

  const filteredFlatProducts = useMemo(
    () => applyProductFilters(allProducts, appliedFilters, i18n.language),
    [allProducts, appliedFilters, i18n.language],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    const isValidObjectId = (id) => /^[a-f\d]{24}$/i.test(id);

    if (!merchantConfigId || !isValidObjectId(merchantConfigId)) {
      log.error('Invalid or missing merchantConfigId:', merchantConfigId);
      return;
    }

    const fetchData = async () => {
      try {
        const { data: resData } = await api.get(`/buyer/store/${merchantConfigId}`, {
          headers:
            user && idToken ? { Authorization: `Bearer ${idToken}` } : {},
          params: {
            adminImpersonateToken: adminImpersonate || null,
          },
        });

        const data = resData.response?.data ?? {};

        if (!data || !data.store || !Array.isArray(data.products)) {
          throw new Error('Malformed store response');
        }

        setStore(data.store);
        setAllProducts(data.products);

        const allCategories = [];

        data.products.forEach(product => {
          const productCats = i18n.language.startsWith('fr')
            ? product.categories?.frNames || []
            : product.categories?.enNames || [];
          productCats.forEach((cat, idx) => {
            if (!allCategories.some(c => c.name === cat)) {
              allCategories.push({
                name: cat,
                sortOrder: product.categories.sortOrder?.[idx] ?? 0,
              });
            }
          });
        });

        allCategories.sort((a, b) => a.sortOrder - b.sortOrder);

        const grouped = {};
        allCategories.forEach(cat => {
          grouped[cat.name] = data.products.filter(product =>
            (i18n.language.startsWith('fr')
              ? product.categories?.frNames
              : product.categories?.enNames
            )?.includes(cat.name),
          );
        });

        setProductsByCategory(grouped);

        setActiveTab(allCategories[0]?.name ?? null);

        categoryRefs.current = allCategories.reduce((acc, cat) => {
          acc[cat.name] = React.createRef();
          return acc;
        }, {});
      } catch (error) {
        log.error('Error fetching merchant details:', error);
      }
    };

    fetchData();
  }, [merchantConfigId, i18n.language]);

  const handleScroll = useCallback(() => {
    if (!categoryRefs.current) {return;}
    const scrollPosition = window.scrollY + 150;
    let currentActive = null;
    Object.entries(categoryRefs.current).forEach(([cat, ref]) => {
      if (!ref.current) {
        return;
      }
      if (scrollPosition >= ref.current.offsetTop) {
        currentActive = cat;
      }
    });
    if (currentActive && currentActive !== activeTab) {
      setActiveTab(currentActive);
    }
  }, [activeTab]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (categoryRefs.current[tab]?.current) {
      const element = categoryRefs.current[tab].current;
      const yOffset = -140;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const openVariantDecisionModal = (data) => {
    setVariantDecision(data);
  };

  const handleVariantQuantityIntent = (product, type) => {
    const { item } = getMaxVariantFromCart(product._id);

    // no variants → normal behavior
    if (!product.variants?.length) {
      changeQuantity(product, type);
      return;
    }

    openVariantDecisionModal({
      product,
      variantItem: item,
      type,
    });
  };

  const handleModifySameVariant = () => {
    if (!variantDecision) {
      return;
    }

    const { product, variantItem, type } = variantDecision;

    changeQuantity(
      {
        ...product,
        variantId: variantItem.variantId,
      },
      type,
    );

    setVariantDecision(null);
  };

  const handleChooseAnotherVariant = () => {
    if (!variantDecision) {
      return;
    }

    setSelectedProduct(variantDecision.product); // opens existing product modal
    setVariantDecision(null);
  };

  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    if (
      typeof lat1 !== 'number' ||
      typeof lon1 !== 'number' ||
      typeof lat2 !== 'number' ||
      typeof lon2 !== 'number'
    ) {
      return null;
    }
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return +(R * c).toFixed(2);
  };

  const getTravelTimeByCar = (distanceKm) => {
    if (typeof distanceKm !== 'number' || distanceKm <= 0) {
      return null;
    }
    const timeHours = distanceKm / DRIVE_SPEED_KMH;
    const timeMinutes = Math.round(timeHours * 60);
    return timeMinutes;
  };

  const displayStoreCategories = (cats) => {
    if (!cats) {
      return '';
    }
    const lang = i18n.language;
    const arr = lang.startsWith('fr') ? cats.frNames : cats.enNames;
    return Array.isArray(arr) && arr.length > 0 ? arr.join(', ') : '';
  };

  return (
    <>
      <div className="flex flex-col min-h-dvh">
        <Header
          storeName={store?.storeName}
          merchantConfigId={merchantConfigId}
        />

        {!store ? (
          <>
            {/* Hero Skeleton */}
            <section>

              {/* Banner */}
              <div className="relative h-[140px] md:h-[230px] bg-gray-200 overflow-hidden rounded">
                <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
              </div>

              <div className="px-3 md:px-7 relative z-10">
                <div className="flex items-center -mt-12 md:-mt-14">

                  {/* Logo */}
                  <div className="relative boxshadowTwo rounded-[20px] bg-gray-300 min-w-[92px] md:w-[120px] h-[92px] md:h-[120px] overflow-hidden flex-shrink-0">
                    <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                  </div>

                  {/* Store text */}
                  <div className="ml-4 flex-1 flex flex-col mt-14 md:mt-16 gap-2">
                    <div className="relative h-5 w-64 bg-gray-300 rounded overflow-hidden">
                      <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                    </div>
                    <div className="relative h-4 w-40 bg-gray-200 rounded overflow-hidden">
                      <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                    </div>
                  </div>

                </div>

                {/* Time + Distance */}
                <div className="mt-2 flex items-center gap-3">
                  <div className="relative h-4 w-12 bg-gray-200 rounded overflow-hidden">
                    <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                  </div>

                  <div className="relative w-1 h-1 rounded-full bg-gray-300 overflow-hidden">
                    <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                  </div>

                  <div className="relative h-4 w-16 bg-gray-200 rounded overflow-hidden">
                    <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                  </div>
                </div>
              </div>
            </section>

            {/* Products & Tabs Skeleton */}
            <div className="pb-28 lg:pb-20">

              {/* Tabs bar */}
              <div className="pb-0 px-3 md:px-7 flex sticky top-[46px] lg:top-16 pt-4 bg-white z-20 gap-4">
                {[1,2,3,4,5,6].map((_, idx) => (
                  <div
                    key={idx}
                    className="
                      relative flex-shrink-0 h-6 bg-gray-200 rounded overflow-hidden
                      w-1/4 lg:w-1/12
                    "
                  >
                    <div
                      className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"
                    ></div>
                  </div>
                ))}
              </div>

              {/* Product rows */}
              {[1].map((_, rowIdx) => (
                <div key={rowIdx} className="px-3 md:px-7 my-6">
                  {/* Category title */}
                  <div className="relative h-6 w-40 bg-gray-300 rounded mb-4 overflow-hidden">
                    <div
                      className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"
                    ></div>
                  </div>

                  {/* Products grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((_, productIdx) => (
                      <div
                        key={productIdx}
                        className="border p-4 rounded flex justify-between relative overflow-hidden cursor-pointer"
                      >
                        {/* Left: text */}
                        <div className="flex flex-col justify-between pr-3 sm:pr-4 flex-1 min-w-0 gap-2">
                          <div className="relative h-5 w-32 max-w-full bg-gray-300 rounded overflow-hidden">
                            <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                          </div>
                          <div className="relative h-4 w-48 max-w-full bg-gray-200 rounded overflow-hidden">
                            <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                          </div>
                          <div className="relative h-4 w-20 max-w-full bg-gray-300 rounded overflow-hidden mt-2">
                            <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                          </div>
                        </div>

                        {/* Right: image */}
                        <div className="relative w-[88px] h-[88px] sm:w-[120px] sm:h-[120px] rounded-lg border bg-gray-200 flex-shrink-0 overflow-hidden">
                          <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

            </div>
          </>
        ) : (
          <>
            {/* Hero */}
            <section className="relative">
              <div className="h-[140px] sm:h-[180px] md:h-[230px] w-full overflow-hidden">
                <img
                  src={store.coverImage || BannerImage}
                  alt={`${store.storeName || 'Store'} banner`}
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>

              <div className="px-3 md:px-7 relative z-10">
                <div className="flex items-center -mt-12 md:-mt-14">
                  <div className="boxshadowTwo rounded-[20px] bg-white border-4 border-white overflow-hidden min-w-[92px] md:w-[120px] h-[92px] md:h-[120px] flex-shrink-0">
                    <img
                      src={store.logoImage}
                      alt={store.storeName || 'Merchant Logo'}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="ml-4 flex-1 w-full flex flex-col mt-14 md:mt-16">
                    <h1 className="font-semibold capitalize text-base md:text-2xl leading-5 md:leading-8 text-[#1F2937] line-clamp-2">
                      {store.storeName || 'Store Name'}
                    </h1>
                    <p className="font-medium capitalize text-xs md:text-sm text-[#909090] mt-1 truncate line-clamp-1">
                      {displayStoreCategories(store.categories) ||
                        t('No categories available')}
                    </p>
                  </div>
                </div>

                {/* Rating + Distance */}
                <div className="mt-2 relative">
                  <div className="font-semibold text-xs md:text-sm flex gap-3 items-center">
                    {(() => {
                      const storeRatingPercent = ratingToPercent(store.rating?.averageRating);
                      return storeRatingPercent && (
                        <p className="flex items-center gap-1">
                          {storeRatingPercent}
                        </p>
                      );
                    })()}

                    {user?.locations?.[0] &&
                      store.address?.latitude != null &&
                      store.address?.longitude != null &&
                      (() => {
                        const distance = getDistanceKm(
                          user.locations[0].latitude,
                          user.locations[0].longitude,
                          store.address.latitude,
                          store.address.longitude,
                        );
                        const travelTime = getTravelTimeByCar(distance);

                        return (
                          <>
                            {store.rating?.averageRating &&
                              travelTime !== null && (
                              <div className="w-1 h-1 rounded-full bg-black"></div>
                            )}
                            {travelTime !== null && (
                              <div>
                                {travelTime} {t('mins')}
                              </div>
                            )}
                            {distance !== null && (
                              <>
                                <div className="w-1 h-1 rounded-full bg-black"></div>
                                <p>
                                  {distance} {t('kilometers')}
                                </p>
                              </>
                            )}
                          </>
                        );
                      })()}
                  </div>
                </div>
              </div>
            </section>

            {/* Products & Tabs */}
            <div className="pb-28 lg:pb-20">
              {(() => {
                const displayedGroups = hasActiveFilters
                  ? (filteredFlatProducts.length > 0
                    ? { [t('Results')]: filteredFlatProducts }
                    : {})
                  : productsByCategory;
                const displayedEntries = Object.entries(displayedGroups);
                if (displayedEntries.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <h3 className="text-lg font-semibold text-gray-500 mb-1">
                        {hasActiveFilters ? t('No products found') : t('No products available')}
                      </h3>
                      {!hasActiveFilters && (
                        <p className="text-sm text-gray-400">{t('This store hasn\'t added any products yet.')}</p>
                      )}
                    </div>
                  );
                }
                return (
                  <>
                    {!hasActiveFilters && (
                      <div className="border-b-2 pb-0 border-gray-700 px-3 md:px-7 flex overflow-x-auto boxscrollbar2 sticky top-[46px] lg:top-16 pt-3 mt-0 bg-white z-20">
                        {Object.keys(productsByCategory).map((cat) => (
                          <button
                            key={cat}
                            onClick={() => handleTabClick(cat)}
                            className={`py-2 px-[15px] capitalize text-sm text-center whitespace-nowrap ${
                              activeTab === cat
                                ? 'border-b-[3px] border-black font-medium'
                                : ''
                            }`}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    )}

                    {displayedEntries.map(([cat, products]) => (
                      <div
                        key={cat}
                        ref={categoryRefs.current[cat]}
                        className="px-3 md:px-7 my-8"
                      >
                        <h2 className="text-xl font-semibold capitalize mb-4 mt-2">
                          {cat}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                          {products.map((product) => (
                            <div
                              key={product._id}
                              className="border p-4 rounded cursor-pointer flex justify-between relative
                          transition-all duration-300 ease-out
                          hover:border-[#dddddd] hover:shadow-md"
                              onClick={() => setSelectedProduct(product)}
                            >
                              {/* Left */}
                              <div className="flex flex-col justify-between pr-3 sm:pr-4 flex-1 min-w-0">
                                <div>
                                  <h3 className="font-semibold text-base sm:text-lg break-words">
                                    {i18n.language.startsWith('fr')
                                      ? product.frName
                                      : product.enName}
                                  </h3>
                                  <p
                                    className="text-sm text-gray-600 line-clamp-3"
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(
                                        (
                                          (i18n.language.startsWith('fr')
                                            ? product.frDescription
                                            : product.enDescription) || ''
                                        ).replace(/\\n/g, '<br />'),
                                      ),
                                    }}
                                  />
                                </div>

                                <p className="mt-2 font-bold text-base leading-tight">
                                  {product?.price !== null && product?.price !== undefined ? (
                                    <span>{product.price.toFixed(2)}$</span>
                                  ) : (
                                    <span className="text-xs text-[#999] leading-tight">
                                      {t('noPriceMessage')}
                                    </span>
                                  )}
                                </p>
                              </div>

                              {/* Right */}
                              <div className="w-[88px] h-[88px] sm:w-[120px] sm:h-[120px] rounded-lg border border-[#E1E1E1] flex items-center justify-center flex-shrink-0 relative">
                                <div className="w-full h-full rounded-lg overflow-hidden relative">
                                  <img
                                    src={product.images?.[0] || BannerImage}
                                    alt={product.enName}
                                    loading="lazy"
                                    className={`w-full h-full object-contain transition-all duration-300
                                ${product.availability !== 'in-stock' ? 'filter grayscale brightness-90' : ''}`}
                                  />

                                  {product.availability !== 'in-stock' && (
                                    <div className="absolute top-0 left-0 w-full bg-black bg-opacity-50 py-1 px-2 flex items-center justify-center rounded-t-lg">
                                      <span className="text-white text-xs sm:text-sm font-semibold text-center">
                                        {t('Out of stock')}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {product.availability === 'in-stock' && product?.price !== null && product?.price !== undefined && (
                                  getMaxVariantFromCart(product._id).quantity > 0 ? (
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-[-12px] flex items-center bg-black text-white border border-black rounded px-2 py-1 text-sm font-medium">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleVariantQuantityIntent(
                                            product,
                                            'decrease',
                                          );
                                        }}
                                        className="px-2 text-lg"
                                      >
                                  −
                                      </button>

                                      <span className="px-3">
                                        {getMaxVariantFromCart(product._id).quantity}
                                      </span>

                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleVariantQuantityIntent(
                                            product,
                                            'increase',
                                          );
                                        }}
                                        className="px-2 text-lg"
                                      >
                                  +
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        if (product.variants?.length > 0) {
                                          return;
                                        }
                                        e.stopPropagation();
                                        addToCart(product);
                                      }}
                                      className="absolute left-1/2 -translate-x-1/2 bottom-[-12px] bg-white text-black text-sm font-medium px-5 py-1.5 rounded border border-[#E1E1E1] hover:bg-[#33b0d4] transition duration-300 whitespace-nowrap capitalize"
                                    >
                                      {t('add')} +
                                    </button>
                                  ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    <Toast message={toastMessage} type={toastType} />

                    {variantDecision && (
                      <div
                        className="fixed inset-0 bg-black/40 flex items-end justify-center z-50"
                        onClick={() => setVariantDecision(null)}
                      >
                        <div
                          className="
                      bg-white w-full max-w-lg
                      p-6
                      shadow-lg
                      animate-slide-up2
                    "
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="-mx-6 -mt-6 mb-4 bg-[#ccc] px-6 py-4">
                            <h3 className="text-lg font-semibold leading-tight">
                              {i18n.language.startsWith('fr')
                                ? variantDecision.product?.frName
                                : variantDecision.product?.enName}
                            </h3>
                          </div>

                          <p className="text-sm mb-6">
                            {t('previousSelections')}:{' '}
                            {i18n.language.startsWith('fr')
                              ? variantDecision.variantItem?.frVariantName
                              : variantDecision.variantItem?.enVariantName}
                          </p>

                          <div className="flex gap-3">
                            <button
                              onClick={handleChooseAnotherVariant}
                              className="flex-1 border border-black bg-white py-3 font-medium hover:bg-gray-100"
                            >
                              {t('chooseVariant')}
                            </button>

                            <button
                              onClick={handleModifySameVariant}
                              className="flex-1 bg-black text-white border border-gray-300 py-3 font-medium"
                            >
                              {t('repeat')}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedProduct && (
                      <SingleProductDetails
                        product={selectedProduct}
                        cartQuantities={getVariantQuantitiesFromCart(selectedProduct._id)}
                        onClose={() => setSelectedProduct(null)}
                      />
                    )}
                  </>
                );
              })()}
            </div>
          </>
        )}

        {uniqueItems > 0 && <CartBar />}
        <MobileCartBar />
      </div>
      <Footer />
    </>
  );
};

export default MerchantsDetails;
